import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./css/dilemmaRiskSelection.css";
import useGameState from "../hooks/useGameState";
import useTranslation from "../hooks/useTranslation";

function DilemmaRiskSelection({ updateProgress }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectedCharacters } = useGameState();
  const players = selectedCharacters?.playersArray || [];
  
  // Función helper para obtener el nombre traducido del personaje
  const getCharacterName = (characterId) => {
    return t(`characterSelection.characters.${characterId}.name`);
  };
  
  const [topRisks, setTopRisks] = useState([]);
  const [isReady, setIsReady] = useState(false);

  // Los 10 riesgos de Awareness
  const allRisks = t('awarenessWrapup.risks');

  useEffect(() => {
    // Leer las selecciones individuales del localStorage
    const playerSelections = JSON.parse(localStorage.getItem('greenscape_risk_selections') || '{}');
    
    // Contar votos por riesgo
    const voteCounts = {};
    Object.values(playerSelections).forEach(risks => {
      risks.forEach(riskIndex => {
        voteCounts[riskIndex] = (voteCounts[riskIndex] || 0) + 1;
      });
    });

    // Convertir a array y ordenar por votos (descendente)
    const sortedRisks = Object.entries(voteCounts)
      .map(([index, votes]) => ({
        index: parseInt(index),
        votes,
        text: allRisks[parseInt(index)],
        players: Object.entries(playerSelections)
          .filter(([_, risks]) => risks.includes(parseInt(index)))
          .map(([characterId]) => players.find(p => p.characterId === characterId))
          .filter(p => p !== undefined)
      }))
      .sort((a, b) => b.votes - a.votes);

    // Tomar los 3 más votados
    const top3 = sortedRisks.slice(0, 3);
    setTopRisks(top3);

    // Guardar los índices de los 3 más votados
    const top3Indices = top3.map(r => r.index);
    localStorage.setItem('greenscape_selected_dilemma_risks', JSON.stringify(top3Indices));
  }, [players]);

  const handleContinue = () => {
    updateProgress('dilema-risk-selection');
    navigate('/game/dilema');
  };

  return (
    <div className="risk-selection-container">
      {/* Header */}
      <div className="risk-selection-header">
        <h1 className="selection-title">
          {t('dilemmaRiskSelection.title')}
        </h1>
        <p className="selection-subtitle">{t('dilemmaRiskSelection.subtitle')}</p>
      </div>

      {/* Instrucciones */}
      <div className="selection-instructions">
        <p>
          {t('dilemmaRiskSelection.paragraph1')}
        </p>
        <div className="challenge-info">
          <h3>{t('dilemmaRiskSelection.challengeTitle')}</h3>
          <ul>
            <li>{t('dilemmaRiskSelection.challenge1')}</li>
            <li>{t('dilemmaRiskSelection.challenge2')}</li>
          </ul>
        </div>
        <p className="highlight-text">
          {t('dilemmaRiskSelection.highlightText')}
        </p>
      </div>

      {/* Top 3 Riesgos seleccionados */}
      <div className="top-risks-container">
        <h2 className="top-risks-title">{t('dilemmaRiskSelection.topRisksTitle')}</h2>
        
        <div className="top-risks-grid">
          {topRisks.map((risk, index) => (
            <div key={risk.index} className="top-risk-card">
              <div className="risk-rank">#{index + 1}</div>
              <div className="risk-content">
                <p className="risk-text">{risk.text}</p>
                <div className="risk-votes">
                  <div className="vote-count">
                    <span className="vote-number">{risk.votes}</span>
                    <span className="vote-label">{risk.votes === 1 ? t('dilemmaRiskSelection.voteLabel') : t('dilemmaRiskSelection.votesLabel')}</span>
                  </div>
                  <div className="risk-voters">
                    {risk.players.map(player => (
                      <img
                        key={player.characterId}
                        src={player.image}
                        alt={getCharacterName(player.characterId)}
                        className="voter-avatar"
                        style={{ borderColor: player.color }}
                        title={getCharacterName(player.characterId)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Confirmación */}
      <div className="selection-confirmation">
        <label className="confirmation-checkbox">
          <input
            type="checkbox"
            checked={isReady}
            onChange={(e) => setIsReady(e.target.checked)}
          />
          <span>{t('dilemmaRiskSelection.confirmationLabel')}</span>
        </label>
        
        <button
          className={`continue-btn ${isReady ? 'active' : 'disabled'}`}
          onClick={handleContinue}
          disabled={!isReady}
        >
          {t('dilemmaRiskSelection.continueButton')}
        </button>
      </div>
    </div>
  );
}

export default DilemmaRiskSelection;



